/// <amd-module name="Reporting/CFDReport/Pickers/ColumnPicker" />
import { BoardColumnsQuery, Column } from "Analytics/Boards/CumulativeFlowDiagramQueries/BoardColumnsQuery";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import * as React from "react";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssComponent } from "VSS/Platform/Layout";
import { Dropdown } from "VSSUI/Dropdown";
import { IListSelection } from "VSSUI/List";
import { DropdownMultiSelection } from "VSSUI/Utilities/DropdownSelection";
import { reportCommand } from "Widgets/CumulativeFlowDiagram/QueryUtilities";
export { Column };
import * as Resources from "Reporting/CFDReport/Resources";
import { format } from "VSS/Core/Util/String";
import { IListBoxItem } from "VSSUI/ListBox";

export interface ColumnPickerProps {
    selectedIds?: string[];
    items: IListBoxItem<Column>[];
    onSelect(columns: IListBoxItem<Column>[]): void;
}

export class ColumnPicker extends VssComponent<ColumnPickerProps> {
    private selection: IListSelection;

    public constructor(props: ColumnPickerProps) {
        super(props);
        this.selection = new DropdownMultiSelection();
    }

    public componentDidMount(): void {
        this.applySelection(this.props);

        this.selection.subscribe(this.onSelectionChange);
    }

    public componentWillReceiveProps(newProps: ColumnPickerProps): void {
        if (JSON.stringify(newProps) != JSON.stringify(this.props)) {
            this.selection.unsubscribe(this.onSelectionChange);
            this.applySelection(newProps);
            this.selection.subscribe(this.onSelectionChange);
        }
    }

    public componentWillUnmount(): void {
        this.selection.unsubscribe(this.onSelectionChange);
    }

    public render(): JSX.Element {
        return (
            <div className="flex-row">
                <Dropdown className="board-column-picker" items={this.props.items} selection={this.selection} />
            </div>
        );
    }

    private applySelection(props: ColumnPickerProps): void {
        // Apply selection from incoming props
        const mergeSelections = true;
        if (props.selectedIds !== undefined) {
            this.selection.clear();
            props.selectedIds.forEach(selectedItem => {
                const index = props.items.findIndex(candidateItem => {
                    return candidateItem.id === selectedItem;
                });
                if (index >= 0) {
                    this.selection.select(index, 1, mergeSelections);
                }
            });
        }
    }

    private onSelectionChange = (): void => {
        const selections = this.selection.value;
        const selectedItems: Set<IListBoxItem<Column>> = new Set();
        for (let i = 0; i < selections.length; i++) {
            for (let j = selections[i].beginIndex; j <= selections[i].endIndex; j++) {
                selectedItems.add(this.props.items[j]);
            }
        }

        this.props.onSelect(Array.from(selectedItems));
    };
}

export async function getColumns(pageContext: IVssPageContext, projectId: string, boardId: string, startDate: Date): Promise<IListBoxItem<Column>[]> {
    const queryCache = pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
    const query = new BoardColumnsQuery(reportCommand, projectId, boardId);
    const { boardColumns } = await queryCache.getCacheableQueryResult(query);
    const orderedColumns = orderBoardColumns(boardColumns);
    const columnsExistingAtStartOfChart = orderedColumns.filter(o => {
        return new Date(o.RevisedDate) > startDate;
    });

    return columnsExistingAtStartOfChart.map(column => {
        return {
            id: column.ColumnId,
            text: column.IsDeleted ? format(Resources.DeletedBoardColumnName, column.ColumnName) : column.ColumnName,
            data: column
        };
    });
}

/**
 * Applies CFD Chart style column ordering, as ported from legacy.
 * @param boardColumns
 */
function orderBoardColumns(boardColumns: ReadonlyArray<Column>): Column[] {
    const activeColumns: Column[] = [];
    const deletedColumns: Column[] = [];

    for (const column of boardColumns) {
        if (column.IsDeleted) {
            deletedColumns.push(column);
        } else {
            activeColumns.push(column);
        }
    }

    // Order deleted columns by deletion date (latest to earliest)
    deletedColumns.sort((a, b) => {
        const revisedDateA = new Date(a.RevisedDate);
        const revisedDateB = new Date(b.RevisedDate);
        return revisedDateB.getTime() - revisedDateA.getTime();
    });

    // Order active columns by column order (last to first)
    activeColumns.sort((a, b) => b.ColumnOrder - a.ColumnOrder);

    // Insert sorted deleted columns immediately after the Done state column of the ordered active columns
    // [startIndex, deleteCount, ...items]
    const orderedColumns = activeColumns.slice();
    orderedColumns.splice(1, 0, ...deletedColumns);

    return orderedColumns;
}

VssComponent.register("BoardsColumnPicker", ColumnPicker);
